import React, { useState, useEffect } from 'react';

/**
 * SupportModal — slide-up modal overlay for submitting a support ticket.
 * Props:
 *   isOpen   boolean
 *   onClose  () => void
 *   user     { email, display_name } (optional, pre-fills fields)
 */
const SupportModal = ({ isOpen, onClose, user }) => {
    const [subject,  setSubject]  = useState('');
    const [message,  setMessage]  = useState('');
    const [sending,  setSending]  = useState(false);
    const [result,   setResult]   = useState(null); // { ok, text }

    // Reset form when re-opened
    useEffect(() => {
        if (isOpen) { setSubject(''); setMessage(''); setResult(null); }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        if (!subject.trim() || !message.trim()) return;
        setSending(true);
        setResult(null);
        try {
            const r = await fetch('/api/support', {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ subject: subject.trim(), message: message.trim() }),
            });
            const data = await r.json();
            if (r.ok) {
                setResult({ ok: true, text: `Ticket #${data.ticketId} submitted — we'll be in touch soon.` });
                setSubject(''); setMessage('');
            } else {
                setResult({ ok: false, text: data.error || 'Failed to submit ticket' });
            }
        } catch (err) {
            setResult({ ok: false, text: 'Network error — please try again.' });
        }
        setSending(false);
    };

    const inp = {
        style: {
            width: '100%', background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
            padding: '10px 14px', color: '#fff', fontSize: 13,
            outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
        },
    };

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed', inset: 0, zIndex: 9998,
                    background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
                }}
            />

            {/* Panel */}
            <div style={{
                position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
                width: '100%', maxWidth: 540,
                zIndex: 9999, background: '#0d0d1a',
                border: '1px solid rgba(255,255,255,0.1)',
                borderBottom: 'none',
                borderRadius: '16px 16px 0 0',
                boxShadow: '0 -16px 64px rgba(0,0,0,0.8)',
                display: 'flex', flexDirection: 'column',
                animation: 'slideUp 0.22s ease-out',
            }}>
                {/* Header */}
                <div style={{
                    padding: '20px 28px 16px',
                    borderBottom: '1px solid rgba(255,255,255,0.07)',
                    display: 'flex', alignItems: 'center',justifyContent: 'space-between',
                }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 900, color: '#fff', letterSpacing: '0.05em' }}>
                            🎫 Request Support
                        </h2>
                        {user?.email && (
                            <p style={{ margin: '4px 0 0', fontSize: 11, color: '#4b5563', fontFamily: 'monospace' }}>
                                {user.email}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        id="support-close-btn"
                        style={{
                            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 8, width: 32, height: 32, cursor: 'pointer',
                            color: '#6b7280', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >✕</button>
                </div>

                {/* Form */}
                <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                        <label style={{ display: 'block', fontSize: 10, color: '#6b7280', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>
                            Subject
                        </label>
                        <input
                            type="text" value={subject}
                            onChange={e => setSubject(e.target.value)}
                            placeholder="e.g. Issue with my credit balance"
                            id="support-subject"
                            maxLength={255}
                            {...inp}
                        />
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: 10, color: '#6b7280', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>
                            Message
                        </label>
                        <textarea
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            placeholder="Describe your issue in as much detail as possible..."
                            id="support-message"
                            maxLength={5000}
                            rows={5}
                            {...inp}
                            style={{ ...inp.style, resize: 'vertical', lineHeight: 1.6 }}
                        />
                        <p style={{ fontSize: 10, color: '#374151', marginTop: 4, textAlign: 'right' }}>
                            {message.length}/5000
                        </p>
                    </div>

                    {/* Result */}
                    {result && (
                        <div style={{
                            padding: '12px 16px', borderRadius: 8,
                            border: `1px solid ${result.ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                            background: result.ok ? 'rgba(16,185,129,0.06)' : 'rgba(239,68,68,0.06)',
                            fontSize: 13,
                            color: result.ok ? '#34d399' : '#f87171',
                        }}>
                            {result.ok ? '✅' : '❌'} {result.text}
                        </div>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 10, paddingBottom: 4 }}>
                        <button
                            onClick={onClose}
                            style={{
                                flex: 1, padding: '11px', borderRadius: 8, cursor: 'pointer',
                                border: '1px solid rgba(255,255,255,0.1)',
                                background: 'transparent', color: '#6b7280', fontSize: 12,
                                fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em',
                            }}
                        >Cancel</button>
                        <button
                            onClick={handleSubmit}
                            disabled={sending || !subject.trim() || !message.trim() || result?.ok}
                            id="support-submit-btn"
                            style={{
                                flex: 2, padding: '11px', borderRadius: 8,
                                cursor: (sending || !subject.trim() || !message.trim() || result?.ok) ? 'not-allowed' : 'pointer',
                                border: 'none',
                                background: (!subject.trim() || !message.trim() || result?.ok)
                                    ? 'rgba(59,130,246,0.2)'
                                    : 'linear-gradient(135deg,#2563eb,#3b82f6)',
                                color: '#fff', fontSize: 12, fontWeight: 900,
                                textTransform: 'uppercase', letterSpacing: '0.15em',
                                opacity: (!subject.trim() || !message.trim()) ? 0.5 : 1,
                                transition: 'all 0.15s',
                            }}
                        >
                            {sending ? '⏳ Submitting…' : result?.ok ? '✓ Submitted' : '🎫 Submit Ticket'}
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes slideUp {
                    from { transform: translateX(-50%) translateY(100%); opacity: 0; }
                    to   { transform: translateX(-50%) translateY(0);    opacity: 1; }
                }
            `}</style>
        </>
    );
};

export default SupportModal;
